import { BookOutlined, DeleteOutlined, SendOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Input, Modal, Popconfirm, Select, Spin, Tooltip } from 'antd';
import { format } from 'date-fns';
import { ContactType } from 'gql/types';
import { FC, useEffect, useState } from 'react';

import {
  CREATE_DONOR_CONTACT_MUTATION,
  DELETE_DONOR_CONTACT_MUTATION,
} from '../../../../../donor/graphql/mutations';
import { GET_DONOR_CONTACTS_QUERY } from '../../../../../donor/graphql/queries';

interface IDonorContact {
  id: string;
  contactType: string;
  notes: string | null;
  createdAt: string;
  user: { firstName: string; lastName: string };
}

interface IDonorContactPopoverProps {
  donorId: string;
  donorName: string;
  hasContacts: boolean;
  isAdmin?: boolean;
  onContactCreated?: () => void;
}

const CONTACT_TYPE_OPTIONS = [
  { value: ContactType.Phone, label: 'Phone' },
  { value: ContactType.Email, label: 'Email' },
  { value: ContactType.Text, label: 'Text' },
  { value: ContactType.InPerson, label: 'In Person' },
  { value: ContactType.Voicemail, label: 'Voicemail' },
  { value: ContactType.Other, label: 'Other' },
];

function parseContactDate(raw: string): string {
  try {
    const d = /^\d+$/.test(raw) ? new Date(parseInt(raw, 10)) : new Date(raw);
    return isNaN(d.getTime()) ? raw : format(d, 'MM/dd/yyyy h:mm a');
  } catch {
    return raw;
  }
}

const DonorContactPopover: FC<IDonorContactPopoverProps> = ({
  donorId,
  donorName,
  hasContacts,
  isAdmin,
  onContactCreated,
}) => {
  const [open, setOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [contactType, setContactType] = useState<ContactType>(
    ContactType.Phone
  );

  const [fetchContacts, { data: contactsData, loading: contactsLoading }] =
    useLazyQuery(GET_DONOR_CONTACTS_QUERY, { fetchPolicy: 'network-only' });

  const [createContact, { loading: saving }] = useMutation(
    CREATE_DONOR_CONTACT_MUTATION
  );
  const [deleteContact] = useMutation(DELETE_DONOR_CONTACT_MUTATION);

  useEffect(() => {
    if (open) fetchContacts({ variables: { donorId } });
  }, [open, donorId, fetchContacts]);

  const contacts: IDonorContact[] = (
    contactsData?.getDonorContacts ?? []
  ).filter((c: IDonorContact) => c.contactType !== 'Flag');

  const handleSave = async () => {
    if (!noteText.trim()) return;
    await createContact({
      variables: { input: { donorId, contactType, notes: noteText.trim() } },
    });
    setNoteText('');
    fetchContacts({ variables: { donorId } });
    onContactCreated?.();
  };

  return (
    <>
      <Tooltip
        title={hasContacts ? 'View/add contact notes' : 'Add contact note'}
      >
        <BookOutlined
          style={{ fontSize: 14, cursor: 'pointer', color: '#1565c0' }}
          onClick={() => setOpen(true)}
        />
      </Tooltip>

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        title={`Contact Log — ${donorName}`}
        footer={null}
        width={420}
        destroyOnClose
      >
        <div
          style={{
            maxHeight: 300,
            minHeight: 80,
            overflowY: 'auto',
            marginBottom: 16,
          }}
        >
          {contactsLoading ? (
            <div style={{ textAlign: 'center', padding: 16 }}>
              <Spin size="small" />
            </div>
          ) : contacts.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: 16,
                color: '#999',
                fontSize: 12,
              }}
            >
              No contact notes yet
            </div>
          ) : (
            contacts.map((c) => (
              <div
                key={c.id}
                style={{
                  padding: '8px 0',
                  borderBottom: '1px solid #f0f0f0',
                  fontSize: 12,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 2,
                  }}
                >
                  <span style={{ fontWeight: 600, color: '#0e4d8f' }}>
                    {c.contactType}
                  </span>
                  <span style={{ color: '#999' }}>
                    {parseContactDate(c.createdAt)}
                  </span>
                </div>
                <div style={{ color: '#2f3a45' }}>{c.notes || '—'}</div>
                <div
                  style={{
                    color: '#999',
                    fontSize: 11,
                    marginTop: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>
                    {c.user.firstName} {c.user.lastName}
                  </span>
                  {isAdmin && (
                    <Popconfirm
                      title="Delete this contact note?"
                      onConfirm={async () => {
                        await deleteContact({ variables: { id: c.id } });
                        fetchContacts({ variables: { donorId } });
                      }}
                      okText="Delete"
                      cancelText="Cancel"
                      okButtonProps={{ danger: true }}
                    >
                      <DeleteOutlined
                        style={{
                          color: '#ff4d4f',
                          cursor: 'pointer',
                          fontSize: 12,
                        }}
                      />
                    </Popconfirm>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <div
          style={{
            borderTop: '1px solid #e8e8e8',
            paddingTop: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', gap: 8 }}>
            <Select
              value={contactType}
              onChange={setContactType}
              options={CONTACT_TYPE_OPTIONS}
              style={{ width: 130 }}
              size="small"
            />
            <Button
              type="primary"
              size="small"
              icon={<SendOutlined />}
              loading={saving}
              disabled={!noteText.trim()}
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
          <Input.TextArea
            rows={2}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add a contact note..."
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                handleSave();
              }
            }}
          />
        </div>
      </Modal>
    </>
  );
};

export default DonorContactPopover;
